import { useState, useEffect, useCallback, useRef } from 'react';
import lib from '../../../assets/utils/lib';

const POLL_MS = 60_000;

/**
 * Poll enrollment-conflict summary for Organizations badge.
 */
export function useEnrollmentConflictsCount(enabled = true, params = {}) {
  const [open, setOpen] = useState(0);
  const [pausedSessions, setPausedSessions] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const enabledRef = useRef(enabled);
  const paramsRef = useRef(params);

  enabledRef.current = enabled;
  paramsRef.current = params;

  const refresh = useCallback(async () => {
    if (!enabledRef.current) {
      setOpen(0);
      setPausedSessions(0);
      setUnavailable(false);
      return { open: 0, pausedSessions: 0 };
    }
    setLoading(true);
    try {
      const result = await lib.getEnrollmentConflictsOpenCount(paramsRef.current || {});
      setOpen(result.open || 0);
      setPausedSessions(result.pausedSessions || 0);
      setUnavailable(Boolean(result.unavailable));
      return result;
    } catch (_) {
      setOpen(0);
      setPausedSessions(0);
      return { open: 0, pausedSessions: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setOpen(0);
      setPausedSessions(0);
      return undefined;
    }
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, refresh, params?.social_no, params?.organization_id]);

  return { open, pausedSessions, unavailable, loading, refresh };
}

export default useEnrollmentConflictsCount;
